import { useError } from '#globalContexts/ErrorContext';

import { useEmailCampaignDetailsContext } from '../../../../contexts/EmailCampaignDetailsContext';
import {
	useApproveEmail,
	useUnapproveEmail,
	useUpdateGeneratedEmail,
	useRegenerateEmail,
} from '../../../../models/aiCampaigns/mutations';

/**
 * Hook to handle email review actions (approve, unapprove, update, regenerate)
 * Centralizes mutation logic and error handling
 */
export const useEmailReviewActions = (campaignId, { emails, setSelectedEmailId, refetchEmails }) => {
	const approveEmailMutation = useApproveEmail(campaignId);
	const unapproveEmailMutation = useUnapproveEmail(campaignId);
	const updateEmailMutation = useUpdateGeneratedEmail(campaignId);
	const regenerateMutation = useRegenerateEmail(campaignId);
	const { showError } = useError();
	const { campaign } = useEmailCampaignDetailsContext();

	const handleApprove = async (emailId) => {
		try {
			await approveEmailMutation.mutateAsync({
				emailId,
				campaignStatus: campaign?.campaignStatus,
			});
			refetchEmails();

			// Move to next email
			const currentIndex = emails.findIndex((e) => e.id === emailId);
			if (currentIndex < emails.length - 1) {
				setSelectedEmailId(emails[currentIndex + 1].id);
			}
		} catch {
			showError({
				title: 'Fehler',
				message: 'Die E-Mail konnte nicht genehmigt werden.',
			});
		}
	};

	const handleUnapprove = async (emailId) => {
		try {
			await unapproveEmailMutation.mutateAsync({ emailId });
			refetchEmails();
		} catch (error) {
			showError({
				error,
				title: 'E-Mail konnte nicht zurückgezogen werden',
			});
		}
	};

	const handleUpdate = async (emailId, subject, content) => {
		try {
			await updateEmailMutation.mutateAsync({
				emailId,
				subject,
				content,
			});
			refetchEmails();
		} catch {
			showError({
				title: 'Fehler',
				message: 'Die E-Mail konnte nicht gespeichert werden.',
			});
		}
	};

	const handleRegenerate = async (emailId) => {
		try {
			await regenerateMutation.mutateAsync(emailId);
			refetchEmails();
		} catch {
			showError({
				title: 'Fehler',
				message: 'Die E-Mail konnte nicht neu generiert werden.',
			});
		}
	};

	return {
		handleApprove,
		handleUnapprove,
		handleUpdate,
		handleRegenerate,
		isApproving: approveEmailMutation.isPending,
		isUnapproving: unapproveEmailMutation.isPending,
		isUpdating: updateEmailMutation.isPending,
		isRegenerating: regenerateMutation.isPending,
	};
};
